import { APP_INITIALIZER, NgModule } from '@angular/core';
import { MAT_DATE_LOCALE } from '@angular/material/core';

import { NgxPrintModule } from 'ngx-print';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './components/header/header.component';
import { FooterComponent } from './components/footer/footer.component';
import { MenuComponent } from './components/menu/menu.component';
import { HomeComponent } from './components/home/home.component';
import { ChangelogComponent } from './components/changelog/changelog.component';
import { LoginComponent } from './components/login/login.component';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import {AuthGuard} from './core/auth/auth.guard';
import {AuthService} from './core/auth/auth.service';
import {ConstantsService} from './services/constants.service';
import { CommonModule, HashLocationStrategy, LocationStrategy } from '@angular/common';
import { ColumnEditorComponent } from './components/column-editor/column-editor.component';
import { FiltersComponent } from './components/shared/filters/filters/filters.component';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatSelectModule} from '@angular/material/select';
import {MatGridListModule} from '@angular/material/grid-list';
import {MatCardModule} from '@angular/material/card';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatInputModule} from '@angular/material/input';
import {MatMenuModule} from '@angular/material/menu';
import {MatButtonModule} from '@angular/material/button';
import {MatToolbarModule} from '@angular/material/toolbar';
import {MatDividerModule} from '@angular/material/divider';
import {MatTooltipModule} from '@angular/material/tooltip';
import { BreadcrumbsComponent } from './components/shared/breadcrumbs/breadcrumbs.component';
import {MatDialogModule} from '@angular/material/dialog';
import {MatSnackBarModule} from '@angular/material/snack-bar';
import {MatIconModule} from '@angular/material/icon';
import { MainComponent } from './components/main/main.component';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TooltipModule } from 'primeng/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { ButtonModule } from 'primeng/button';
import { TabMenuModule } from 'primeng/tabmenu';
import { TabViewModule } from 'primeng/tabview';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { SpinnerModule } from 'primeng/spinner';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { CalendarModule } from 'primeng/calendar';
import { TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { SliderModule } from 'primeng/slider';
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownModule } from 'primeng/dropdown';
import { FieldsetModule } from 'primeng/fieldset';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { GalleriaModule } from 'primeng/galleria';
import { InputNumberModule } from 'primeng/inputnumber';
import { DialogModule } from 'primeng/dialog';
import { ListboxModule } from 'primeng/listbox';
import { MenubarModule } from 'primeng/menubar';
import { PaginatorModule } from 'primeng/paginator';
import { InputTextModule } from 'primeng/inputtext';
import { FileUploadModule } from 'primeng/fileupload';
import { PanelModule } from 'primeng/panel';
import { LightboxModule } from 'primeng/lightbox';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { PickListModule } from 'primeng/picklist';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { InputSwitchModule } from 'primeng/inputswitch';
import { MessageModule } from 'primeng/message';
import { SelectButtonModule } from 'primeng/selectbutton';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DialogService } from 'primeng/dynamicdialog';
import { MessageService } from 'primeng/api';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { GlobalLoaderComponent } from './core/global-loader/global-loader.component';
import { ConfigService } from './core/config/config.service';
import { initConfig } from './core/config/config.initializer';
import { ThemeService } from './services/theme.service';
import { AppIconsService } from './services/app-icons.service';
import { ErrorInterceptor } from './core/interceptors/error.interceptor';
import { LoadingInterceptor } from './core/interceptors/loading.interceptor';
import { ApiBaseInterceptor } from './core/interceptors/api-base.interceptor';
import { SharedModule } from './shared/shared.module';
import { ChatWidgetComponent } from './features/chat/components/chat-widget/chat-widget.component';
import { NewConversationDialogComponent } from './features/chat/components/new-conversation-dialog/new-conversation-dialog.component';
import { MatBadgeModule } from '@angular/material/badge';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { TranslatedPaginatorIntl } from './core/i18n/translated-paginator-intl';

export function HttpLoaderFactory(http: HttpClient) {
  // Cache-buster: bez niego przegladarka trzyma stary pl/en/es.json po deployu,
  // a ngx-translate przy braku klucza spada do jezyka domyslnego - stad polskie
  // etykiety na hiszpanskim UI po kazdym dodaniu tlumaczen (zgloszenie 10.08,
  // "Canal de venta" wyswietlane jako "Kanal sprzedazy"). Ten sam wzorzec co
  // changelog.json (?t=). Znacznik liczony raz na start aplikacji.
  return new TranslateHttpLoader(http, './assets/i18n/', '.json?cb=' + Date.now());
}


@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    MenuComponent,
    ChatWidgetComponent,
    NewConversationDialogComponent,
    HomeComponent,
    ChangelogComponent,
    LoginComponent,
    ColumnEditorComponent,
    FiltersComponent,
    BreadcrumbsComponent,
    MainComponent,
    GlobalLoaderComponent,
  ],
  imports: [
    MatProgressBarModule,
    MatButtonToggleModule,
    MatCheckboxModule,
    MatBadgeModule,
    CommonModule,
    BrowserModule,
    SharedModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    ButtonModule,
    TabMenuModule,
    TabViewModule,
    HttpClientModule,
    FormsModule,
    SpinnerModule,
    InputTextareaModule,
    CalendarModule,
    TableModule,
    ToastModule,
    ReactiveFormsModule,
    SliderModule,
    MultiSelectModule,
    DropdownModule,
    FieldsetModule,
    ConfirmDialogModule,
    DialogModule,
    ListboxModule,
    MenubarModule,
    PaginatorModule,
    InputTextModule,
    FileUploadModule,
    PanelModule,
    LightboxModule,
    GalleriaModule,
    OverlayPanelModule,
    InputNumberModule,
    TooltipModule,
    NgxPrintModule,
    PickListModule,
    ProgressSpinnerModule,
    InputSwitchModule,
    MatFormFieldModule,
    MatSelectModule,
    MatGridListModule,
    MatCardModule,
    MessageModule,
    MatAutocompleteModule,
    MatInputModule,
    SelectButtonModule,
    AutoCompleteModule,
    MatMenuModule,
    MatButtonModule,
    MatToolbarModule,
    MatDividerModule,
    MatTooltipModule,
    MatDialogModule,
    MatSnackBarModule,
    MatIconModule,
    MatSlideToggleModule,
    TranslateModule.forRoot({
      defaultLanguage: 'pl',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
  ],
  providers: [AuthService, AuthGuard, DialogService, MessageService, ConstantsService, {provide: LocationStrategy, useClass: HashLocationStrategy},
    // napisy paginatora w jezyku panelu (dotad zawsze po angielsku)
    { provide: MatPaginatorIntl, useClass: TranslatedPaginatorIntl },
    {
      provide: APP_INITIALIZER,
      useFactory: initConfig,
      deps: [ConfigService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (theme: ThemeService) => () => theme.init(),
      deps: [ThemeService],
      multi: true,
    },
    {
      // wlasne ikony akcji dokumentowych — patrz AppIconsService
      provide: APP_INITIALIZER,
      useFactory: (icons: AppIconsService) => () => icons.register(),
      deps: [AppIconsService],
      multi: true,
    },

    // Bez tego wszystkie mat-datepicker w aplikacji renderują daty w formacie
    // en-US (7/21/2026) - NativeDateAdapter domyślnie bierze locale
    // przeglądarki/Intl, nie ma go skąd wziąć bez jawnego providera.
    // en-GB (nie pl-PL) celowo - daje dd/MM/yyyy ze slashami (21/07/2026),
    // dokładnie ten format o który prosił user; pl-PL formatuje z kropkami
    // (21.07.2026).
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiBaseInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: LoadingInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
